import { SortTypeEnum } from "@repo/models";
import { Column, GridPreLoaded, OriginalTimezoneDateColumn } from "@repo/ui";
import { FC, useCallback, useMemo } from "react";

import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { useTrainingQueries } from "../../../api/trainingApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { useSegmentContext } from "../../../contexts/segmentContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import MindflashCourse from "../../../models/mindflashCourse";

const COLUMNS: Column<MindflashCourse>[] = [
	new Column("Course Name", "courseName"),
	new Column("Status", "status"),
	new Column("Required", "required").withBooleanFormat().withSortable(false),
	new OriginalTimezoneDateColumn("Invitation Date", "invitationDate").dateOnly().withSortBy(SortTypeEnum.ascending),
	new OriginalTimezoneDateColumn("Last Activity", "lastActivity").dateOnly(),
	new OriginalTimezoneDateColumn("Completion Date", "completionDate").dateOnly(),
	new OriginalTimezoneDateColumn("Due Date", "dueDate").dateOnly(),
	new Column("Final Grade", "finalGrade").withSortable(false),
];

export const MindFlashGrid: FC = () => {
	const { repId } = useRepContext();
	const [settings] = useSettingsQuery();
	const { multiStateEnabled } = settings;
	const { useRepSegmentsQuery, getRepSegment } = useRepSegmentsApi(repId);
	const { segmentId } = useSegmentContext();
	const { data: repSegments, loading: repSegmentsLoading } = useRepSegmentsQuery();
	const { useMindFlashQuery } = useTrainingQueries();
	const { data: trainings, loaded, clearQuery } = useMindFlashQuery();

	const loading = useMemo(() => !loaded || repSegmentsLoading, [loaded, repSegmentsLoading]);

	const columns = useMemo(() => {
		const allColumns = [...COLUMNS];

		if (multiStateEnabled && !repSegmentsLoading)
			allColumns.push(
				new Column("Onboarding State", "onboardingSegmentIds").withFormat((ids: number[]) => {
					const noSegment = !ids.length;
					const allSegments = ids.length > 1 && ids.length === repSegments.length;
					if (noSegment || allSegments) return "Global";
					return ids
						.map(getRepSegment)
						.map((s) => s?.name)
						.filter((s) => s)
						.join(" | ");
				}),
			);

		return allColumns;
	}, [multiStateEnabled, repSegmentsLoading]);

	const inSegment = useCallback(
		({ onboardingSegmentIds }: MindflashCourse) => {
			//if no segment selected, show all
			if (!segmentId) return true;
			//if in segment, show
			return onboardingSegmentIds?.includes(segmentId);
		},
		[segmentId],
	);

	const filteredData = useMemo(() => {
		return (trainings?.data ?? []).filter(inSegment);
	}, [trainings, inSegment]);

	return (
		<GridPreLoaded
			title={null}
			id="mfCourseGrid"
			keyProperty="courseId"
			pageSize={3}
			sortable
			columns={columns}
			values={filteredData}
			loading={loading}
			getData={clearQuery}
		/>
	);
};
